import React, { useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Mail, Lock, AlertCircle, Truck, Eye, EyeOff, Zap, PackagePlus, Gavel, CheckCircle2 } from 'lucide-react';
import { resendVerification } from '../services/api';
import { WhatsAppHelpText } from '../components/WhatsAppButton';

export default function LoginPage() {
  const { user, loading, login } = useAuth();
  const [searchParams] = useSearchParams();
  const prefillEmail = searchParams.get('email') || '';
  const nextPath = searchParams.get('next');
  const [email, setEmail] = useState(prefillEmail);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showResend, setShowResend] = useState(false);
  const [resending, setResending] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  // Keep me signed in — default ON. Controls whether the auth cookies are
  // persistent (30 days) or session-only (cleared when the browser closes,
  // for shared devices).
  const [stayLoggedIn, setStayLoggedIn] = useState(true);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <img src="/logo.png" alt="JustDeliver" className="h-9 w-auto animate-pulse" />
      </div>
    );
  }

  if (user) {
    const defaultPath = user.role === 'admin' ? '/admin' : user.role === 'driver' ? '/driver' : '/customer';
    // Honour ?next= for claim/redirect flows (only customer-side paths).
    // Fall back to a localStorage redirect stashed by RegisterPage if the
    // URL parameter was lost (e.g. user verified email in a new tab and then
    // clicked Login).
    let pendingRedirect = nextPath;
    if (!pendingRedirect) {
      try {
        // Peek only — CustomerDashboard's claim effect owns cleanup so the
        // redirect survives if the user bounces back through /login.
        pendingRedirect = localStorage.getItem('postSignupRedirect');
      } catch (e) { /* storage disabled */ }
    }
    const safeNext = pendingRedirect && pendingRedirect.startsWith('/customer') ? pendingRedirect : defaultPath;
    return <Navigate to={safeNext} replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    const result = await login(email, password, stayLoggedIn);
    
    if (!result.success) {
      setError(result.error);
      if (result.error && result.error.toLowerCase().includes('verify')) {
        setShowResend(true);
      }
    }
    
    setIsLoading(false);
  };

  const handleResend = async () => {
    setResending(true);
    try {
      await resendVerification(email);
      setError('Verification email sent! Please check your inbox.');
      setShowResend(false);
    } catch (e) {}
    setResending(false);
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-white relative overflow-hidden border-r border-gray-100">
        <div className="relative z-10 flex flex-col justify-center items-center w-full p-12">
          <img src="/logo.png" alt="JustDeliver" className="h-[52px] w-auto mb-8" />
          <h2 className="text-4xl font-bold font-['Inter'] mb-4 text-center text-gray-900 tracking-tight leading-tight">
            You Post. <span className="text-[#2E7D32]">Drivers Bid.</span> You Choose<span className="text-[#F57C00]">.</span>
          </h2>
          <p className="text-gray-600 text-lg text-center max-w-md">
            Post your delivery and receive competitive bids from independent drivers. Choose the driver and price that suits you.
          </p>

          {/* How the marketplace works */}
          <div className="mt-12 grid grid-cols-3 gap-6 text-center" data-testid="login-marketplace-steps">
            <div>
              <div className="w-12 h-12 bg-[#2E7D32]/10 rounded-full flex items-center justify-center mx-auto mb-2">
                <PackagePlus className="w-6 h-6 text-[#2E7D32]" />
              </div>
              <p className="text-sm font-semibold text-gray-700">Post Your Delivery</p>
            </div>
            <div>
              <div className="w-12 h-12 bg-[#F57C00]/10 rounded-full flex items-center justify-center mx-auto mb-2">
                <Gavel className="w-6 h-6 text-[#F57C00]" />
              </div>
              <p className="text-sm font-semibold text-gray-700">Get Driver Bids</p>
            </div>
            <div>
              <div className="w-12 h-12 bg-[#1565C0]/10 rounded-full flex items-center justify-center mx-auto mb-2">
                <CheckCircle2 className="w-6 h-6 text-[#1565C0]" />
              </div>
              <p className="text-sm font-semibold text-gray-700">Choose Your Driver</p>
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="flex justify-center mb-8 lg:hidden">
            <img src="/logo.png" alt="JustDeliver" className="h-10 w-auto" />
          </div>

          <Card className="border border-gray-200 shadow-none">
            <CardHeader className="space-y-1 pb-4">
              <CardTitle className="text-2xl font-semibold font-['Inter']">Welcome back</CardTitle>
              <CardDescription className="text-gray-600">
                Enter your credentials to access your account
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {error && (
                  <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="login-error">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    <span>{error}</span>
                  </div>
                )}
                {showResend && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleResend}
                    disabled={resending}
                    className="w-full border-[#2E7D32] text-[#2E7D32]"
                    data-testid="resend-verification-login-btn"
                  >
                    {resending ? 'Sending...' : 'Resend Verification Email'}
                  </Button>
                )}

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm font-medium">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10 rounded-sm focus:ring-2 focus:ring-[#2E7D32] focus:border-[#2E7D32]"
                      required
                      data-testid="login-email-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password" className="text-sm font-medium">Password</Label>
                    <Link to="/forgot-password" className="text-xs text-[#2E7D32] hover:underline font-medium" data-testid="forgot-password-link">
                      Forgot password?
                    </Link>
                  </div>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-10 pr-10 rounded-sm focus:ring-2 focus:ring-[#2E7D32] focus:border-[#2E7D32]"
                      required
                      data-testid="login-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      data-testid="login-password-toggle"
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                {/* Keep me signed in — defaults ON so most users stay logged
                 * in across browser restarts; unchecking switches the auth
                 * cookies to session-only for shared devices. */}
                <label
                  htmlFor="stay-logged-in"
                  className="flex items-center gap-2.5 cursor-pointer select-none -mt-2"
                  data-testid="login-stay-logged-in"
                >
                  <input
                    id="stay-logged-in"
                    type="checkbox"
                    checked={stayLoggedIn}
                    onChange={(e) => setStayLoggedIn(e.target.checked)}
                    className="w-4 h-4 rounded border-gray-300 text-[#2E7D32] focus:ring-2 focus:ring-[#2E7D32]/30 cursor-pointer"
                    data-testid="login-stay-logged-in-checkbox"
                  />
                  <span className="text-sm text-gray-700">
                    Keep me signed in
                    <span className="block text-[11px] text-gray-500 leading-tight mt-0.5">
                      Recommended on personal devices. Uncheck on shared devices.
                    </span>
                  </span>
                </label>

                <Button 
                  type="submit" 
                  className="w-full bg-[#2E7D32] text-white hover:bg-[#1B5E20] rounded-sm h-11"
                  disabled={isLoading}
                  data-testid="login-submit-btn"
                >
                  {isLoading ? 'Signing in...' : 'Sign In'}
                </Button>

                <p className="text-center text-sm text-gray-600">
                  Don't have an account?{' '}
                  <Link to="/register" className="text-[#2E7D32] font-semibold hover:underline" data-testid="login-register-link">
                    Register
                  </Link>
                </p>
                <WhatsAppHelpText />
              </form>

              {/* Divider */}
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200" />
                </div>
                <div className="relative flex justify-center text-xs">
                  <span className="bg-white px-2 text-gray-400 uppercase tracking-wide font-semibold">or</span>
                </div>
              </div>

              {/* Quick Post CTA — no account required */}
              <Link to="/quick-post" data-testid="login-quick-post-cta">
                <Button
                  type="button"
                  className="w-full h-12 font-bold bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white rounded-sm shadow-md shadow-amber-200"
                >
                  <Zap className="w-4 h-4 mr-2" fill="currentColor" /> Quick Post Delivery
                </Button>
              </Link>
              <p className="mt-2 text-center text-xs text-gray-500">
                Post a delivery in under 30 seconds — no account needed.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
